import React from "react";
import clsx from "clsx";
import stripMarkdown from "strip-markdown";
import Link from "next/link";
import { Button, ContentCard, Text } from "@jasperlabs/ui";
import Image from "next/image";

import { useActiveTenant } from "@/tenants/TenantProvider";
import { withMailtoSubject } from "@/tenants/withMailtoSubject";
import Avatar from "@/components/organisms/Avatar";
import RenderMarkdown from "@/components/organisms/RenderMarkdown";

type OfferCardProps = {
	title: string;
	blurb: string;
	imageUrl?: string | null;
	tags?: React.ReactNode;
	href?: string;
	grayscale?: boolean;
	"data-testid"?: string;
	details?:
		| {
				index: number;
				key: string;
				value: string;
		  }[]
		| null;
};

const MAX_BLURB_LENGTH = 1200;

function trimBlurb(blurb: string) {
	if (blurb.length > MAX_BLURB_LENGTH) {
		return `${blurb.substring(0, MAX_BLURB_LENGTH)}...`;
	}

	return blurb;
}

export function OfferCard({
	title,
	blurb,
	imageUrl,
	tags,
	href,
	grayscale = false,
	"data-testid": testId,
	details,
}: OfferCardProps) {
	const tenant = useActiveTenant();
	return (
		<ContentCard data-testid={testId}>
			<ContentCard.Media>
				{imageUrl ? (
					<Image
						src={imageUrl}
						alt={title}
						width={1440}
						height={720}
						className={clsx(
							"w-full h-full absolute object-cover top-0 left-0 z-0",
							grayscale && "grayscale",
						)}
					/>
				) : (
					<ContentCard.MediaFallback>
						<Avatar name={title} size="large" />
					</ContentCard.MediaFallback>
				)}
				<ContentCard.Tags>{tags}</ContentCard.Tags>
			</ContentCard.Media>
			<ContentCard.Body>
				<ContentCard.Content>
					<ContentCard.Title>{title}</ContentCard.Title>
					{details && (
						<ContentCard.Details
							items={details.map((detail) => ({
								key: detail.key,
								value: detail.value,
							}))}
						/>
					)}
					<ContentCard.Description>
						<RenderMarkdown
							text={trimBlurb(blurb)}
							options={{
								remarkPlugins: [stripMarkdown],
								components: {
									p: ({ children }) => (
										<Text variant="body-default" className="mb-2">
											{children}
										</Text>
									),
								},
							}}
						/>
					</ContentCard.Description>
				</ContentCard.Content>
				<ContentCard.Footer>
					<Button
						asChild
						variant="outline-investor-portal"
						weight="medium"
						size="lg"
						className="flex-1 basis-36"
					>
						<a href={withMailtoSubject(tenant.supportUrl, `Enquiry: ${title}`)}>
							Contact us
						</a>
					</Button>
					{href && (
						<Link href={href} className="flex-1 basis-36">
							<Button
								variant="outline-investor-portal"
								weight="medium"
								size="lg"
								className="w-full"
							>
								Apply now
							</Button>
						</Link>
					)}
				</ContentCard.Footer>
			</ContentCard.Body>
		</ContentCard>
	);
}

export function OfferCardLoading() {
	return <ContentCard.Skeleton />;
}
